import endpoints from '@app/src/api/endpoints'
import { useCreateResource } from '@app/src/api/updateHooks'
import TextField from '@app/src/components/Ui/TextField'
import { useSnackbar } from '@app/src/context/SnackbarContext'
import useErrorNotification from '@app/src/hooks/errorNotification'
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from '@mui/material'
import React, { useState } from 'react'
import { useIntl } from 'react-intl'

interface ShareDataComingSoonDialogProps {
  open: boolean
  onClose: () => void
}

const ShareDataComingSoonDialog: React.FC<ShareDataComingSoonDialogProps> = ({ open, onClose }) => {
  const { formatMessage } = useIntl()
  const { showSnackbar } = useSnackbar()
  const { showErrorNotification } = useErrorNotification()
  const [answer, setAnswer] = useState('')
  const { mutateAsync, isPending: isSubmitting } = useCreateResource<void, { answer: string }>()

  const handleClose = (): void => {
    setAnswer('')
    onClose()
  }

  const handleSubmit = async (): Promise<void> => {
    await mutateAsync(
      { url: endpoints.submitShareDataFeedback, body: { answer } },
      {
        onSuccess: () => {
          showSnackbar({
            message: formatMessage({ id: 'assessments.shareDataFeedbackSubmitSuccess' }),
            severity: 'success',
          })
          handleClose()
        },
        onError: error => {
          showErrorNotification({ requestError: error })
        },
      },
    )
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{formatMessage({ id: 'assessments.shareDataTitle' })}</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Typography variant="body1" color="text.secondary">
            {formatMessage({ id: 'assessments.shareDataComingSoonDescription' })}
          </Typography>
          <TextField
            hoveringLabel
            multiline
            rows={4}
            label={formatMessage({ id: 'assessments.shareDataAnswerLabel' })}
            placeholder={formatMessage({ id: 'textFieldPlaceholders.writeHere' })}
            value={answer}
            onChange={e => setAnswer(e.target.value)}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{formatMessage({ id: 'general.close' })}</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={!answer.trim() || isSubmitting}>
          {formatMessage({ id: 'general.submit' })}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ShareDataComingSoonDialog
